import { useCallback, useEffect, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import { RowDoubleClickedEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { Dialog, DialogContent, DialogTitle, makeStyles, Theme } from "@material-ui/core";
import { BookingKitSearchItemFragment } from "@bonliva-crm/graphql";
import { DataGrid, usePolling } from "@bonliva-crm/ui";
import { useDialog, useGlobalStore } from "@hooks";
import { KIT_SLIDER, KitProfileLocation } from "@navigation";
import { useInitialColumnDefs } from "./use-initial-column-defs";
import { useColumnNameSelector } from "./use-column-name-selector";
import { useBookingKitSearchFilters } from "../../hooks/use-booking-kit-search-filters";
import { useBookingKitTableDatasource } from "./use-booking-kit-table-datasource";
import { BookingKitTopBar } from "../booking-kit-top-bar";
import { BookingKitStatusType } from "@bonliva-crm/shared";
import { ListUserSelector } from "@containers/list-user-selector/list-user-selector";
import locale from "@bonliva-crm/locale";
import { AssignToKitDialogData } from "./assign-to-kit-col";
import { isCellRendererLink } from "@common/renderers";

export const BookingKitTable = () => {
  const { queryInput } = useBookingKitSearchFilters();
  const columnNameSelector = useColumnNameSelector();
  const gridRef = useRef<AgGridReact>(null);
  const classes = useStyles();
  const { navigation } = useGlobalStore();

  const queryInputRef = useRef<any>();

  useEffect(() => {
    queryInputRef.current = queryInput;
  }, [queryInput]);

  const dialog = useDialog<AssignToKitDialogData>();
  const handleAssign = useCallback(
    async (userId: string) => {
      try {
        await dialog.data?.onSelect(userId);
        dialog.onClose();
      } catch (e) {
        console.error(e);
      }
    },
    [dialog]
  );

  const colDefs = useInitialColumnDefs(dialog);

  // todo fix this
  usePolling(
    () => {
      gridRef.current?.api?.refreshInfiniteCache();
    },
    "/kits",
    { interval: 5000, refetchOnPushState: true }
  );

  const onRowClicked = useCallback(
    ({ data, event }: RowDoubleClickedEvent<BookingKitSearchItemFragment>) => {
      const target = event.target as HTMLElement;

      if (
        target &&
        !isCellRendererLink(target) &&
        (event.defaultPrevented || target.tagName === "A")
      ) {
        return;
      }

      //todo fix ID
      return navigation.open(KIT_SLIDER, new KitProfileLocation(data._id));
    },
    [navigation]
  );

  const datasource = useBookingKitTableDatasource({
    getGridRef: () => gridRef.current
  });

  return (
    <div className={classes.root}>
      <DataGrid
        ref={gridRef}
        columnDefs={colDefs}
        rowSelection={"multiple"}
        onRowClicked={onRowClicked}
        suppressRowClickSelection={true}
        datasource={datasource}
        rowModelType={"infinite"}
        maxBlocksInCache={1}
        cacheBlockSize={50}
        paginationPageSize={50}
        paginationPageSizeSelector={[50, 20, 10]}
        pagination={true}
        getRowId={(p) => p.data._id}
        animateRows={true}
        rowHeight={70}
        getRowClass={(params) => {
          if (params.data?.isHighPriority) {
            return classes.highPriorityRow;
          }

          if (params.data?.lastStatusType) {
            const colorByStatus = getRowColor(params.data.lastStatusType);

            if (colorByStatus && classes[colorByStatus]) {
              return classes[colorByStatus];
            }
          }

          if (!params.data?._id) {
            return classes.hiddenRow;
          }
        }}
        columnNameSelector={columnNameSelector}
        suppressCellFocus={true}
        context={{ highlightText: queryInput.text }}
        topBar={<BookingKitTopBar />}
      />
      <Dialog fullWidth maxWidth="md" open={dialog.open} onClose={dialog.onClose}>
        <DialogTitle>{locale.addPersons}</DialogTitle>
        <DialogContent>
          <ListUserSelector
            multi={false}
            value={[]}
            onSelect={(v) => handleAssign(v[0]._id)}
            ListSelectorProps={{
              searchAutofocus: false,
              withSearch: true
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const getRowColor = (status: BookingKitStatusType) => {
  switch (status) {
    case BookingKitStatusType.Done:
      return "doneRow";
    case BookingKitStatusType.Deleted:
      return "deletedRow";
    case BookingKitStatusType.RejectedByOC:
    case BookingKitStatusType.RejectedByFinancePayment:
    case BookingKitStatusType.RejectedByFinanceInvoicing:
    case BookingKitStatusType.RejectedByHBA:
      return "rejectedRow";
    default:
      return "";
  }
};

export const rowClasses = (theme: Theme) => ({
  deletedRow: {
    backgroundColor: theme.palette.info.light
  },
  rejectedRow: {
    backgroundColor: "#FFD4D4"
  },
  doneRow: {
    backgroundColor: "#D5E6C8"
  },
  approvedRow: {
    backgroundColor: theme.palette.success.light
  }
});

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    paddingBottom: "1rem"
  },
  highPriorityRow: {
    backgroundColor: "#f3e4aa"
  },
  hiddenRow: {
    display: "none"
  },
  ...rowClasses(theme)
}));
